/**
 * Table Utilities
 * 
 * Helper functions for table manipulation
 */

import { Selection, Transaction } from '@tiptap/pm/state';
import { Node, ResolvedPos } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';

export interface TableResult {
    node: Node;
    pos: number;
    start: number;
}

/**
 * Find the table node at the current selection
 */
export function findTable(selection: Selection): TableResult | undefined {
    const { $head } = selection;

    for (let depth = $head.depth; depth > 0; depth--) {
        const node = $head.node(depth);
        if (node.type.name === 'table') {
            return {
                node,
                pos: $head.before(depth),
                start: $head.start(depth),
            };
        }
    }

    return undefined;
}

/**
 * Find the cell at the current selection
 */
export function findTableCell(selection: Selection): { node: Node; pos: number; start: number } | undefined {
    const { $head } = selection;

    for (let depth = $head.depth; depth > 0; depth--) {
        const node = $head.node(depth);
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
            return {
                node,
                pos: $head.before(depth),
                start: $head.start(depth),
            };
        }
    }

    return undefined;
}

/**
 * Get all cells in a specific row
 */
export function getCellsInRow(rowIndex: number) {
    return (selection: Selection): { pos: number; node: Node }[] | undefined => {
        const table = findTable(selection);
        if (!table) return undefined;

        const map = TableMap.get(table.node);
        if (rowIndex < 0 || rowIndex >= map.height) return undefined;

        const cells: { pos: number; node: Node }[] = [];
        const seen = new Set<number>();

        for (let col = 0; col < map.width; col++) {
            const cellPos = map.map[rowIndex * map.width + col];
            if (!seen.has(cellPos)) {
                seen.add(cellPos);
                const cell = table.node.nodeAt(cellPos);
                if (cell) {
                    cells.push({ pos: table.start + cellPos, node: cell });
                }
            }
        }

        return cells;
    };
}

/**
 * Get all cells in a specific column
 */
export function getCellsInColumn(colIndex: number) {
    return (selection: Selection): { pos: number; node: Node }[] | undefined => {
        const table = findTable(selection);
        if (!table) return undefined;

        const map = TableMap.get(table.node);
        if (colIndex < 0 || colIndex >= map.width) return undefined;

        const cells: { pos: number; node: Node }[] = [];
        const seen = new Set<number>();

        for (let row = 0; row < map.height; row++) {
            const cellPos = map.map[row * map.width + colIndex];
            if (!seen.has(cellPos)) {
                seen.add(cellPos);
                const cell = table.node.nodeAt(cellPos);
                if (cell) {
                    cells.push({ pos: table.start + cellPos, node: cell });
                }
            }
        }

        return cells;
    };
}

/**
 * Get the table map dimensions
 */
export function getTableSize(selection: Selection): { rows: number; cols: number } | undefined {
    const table = findTable(selection);
    if (!table) return undefined;

    const map = TableMap.get(table.node);
    return { rows: map.height, cols: map.width };
}

/**
 * Check if a row is a header row
 */
export function isHeaderRow(rowIndex: number, selection: Selection): boolean {
    const table = findTable(selection);
    if (!table) return false;

    const row = table.node.child(rowIndex);
    if (!row) return false;

    // Check if all cells in the row are header cells
    for (let i = 0; i < row.childCount; i++) {
        if (row.child(i).type.name !== 'tableHeader') {
            return false;
        }
    }

    return true;
}
